import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule } from '@angular/forms';
import { catchError, of, switchMap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PatientService } from '../../core/services/patient.service';
import { Patient } from '../../core/models/patient.model';
import { ClinicPatient } from '../../core/models/clinic-patient.model';
import { EmergencyContact } from '../../core/models/emergency-contact.model';
import { NotificationService } from '../../core/services/notification.service';
import { Gender } from '../../core/enums/gender.enum';
import { UserTitle } from '../../core/enums/user-title.enum';
import { MaritalStatus } from '../../core/enums/marital-status.enum';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { User } from '../../core/models/user.model';
import { UserManagementService } from '../../core/services/user-management.service';
import { EmployeeClinicMembershipDto } from '../../core/models/employee-clinic-membership.dto';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-create-patient',
  templateUrl: './create-patient.html',
  imports: [ReactiveFormsModule, PortalNavigation, CommonModule],
  styleUrl: './create-patient.scss',
})
export class CreatePatient implements OnInit {
  patientForm: FormGroup;
  emergencyContactForm: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  isEditMode = false;
  emergencyContactSameAsPayer = false;
  payerIsSameAsPatient = false;
  patientId: string | null = null;
  existingEmergencyContact: EmergencyContact | null = null;
  userProfile: User | null = null;
  activeRole = 'DOCTOR';

  /** Doctor membership options for reception users (Q2). */
  linkedDoctors: EmployeeClinicMembershipDto[] = [];
  selectedMembershipId: string | null = null;
  isDoctorListLoading = false;

  readonly primaryNavigationItems: PortalNavigationItem[] = [
    { label: 'Patients', icon: 'bi-people-fill', route: '/dashboard/patients-view' },
    { label: 'Bookings', icon: 'bi-calendar-check-fill', route: '/dashboard/booking-list' },
  ];

  get portalName(): string {
    return `${this.activeRole.charAt(0)}${this.activeRole.slice(1).toLowerCase()} Portal`;
  }
  get portalIcon(): string {
    return this.activeRole === 'MANAGER' ? 'bi-clipboard2-pulse-fill' : 'bi-heart-pulse-fill';
  }
  get portalClass(): string {
    return this.activeRole === 'MANAGER' ? 'text-danger' : 'text-primary';
  }
  get profileRoute(): string {
    return this.activeRole === 'DOCTOR' ? '/dashboard/doctor-profile' : '/dashboard/profile';
  }

  /** True only when the logged-in user is a receptionist (not a doctor or manager). */
  get isReception(): boolean {
    return this.activeRole === 'RECEPTION';
  }

  genderOptions = Object.values(Gender);
  titleOptions = Object.values(UserTitle);
  maritalStatusOptions = Object.values(MaritalStatus);
  readonly medicalAidSchemes = [
    'Bestmed',
    'Bonitas',
    'Discovery Health',
    'Fedhealth',
    'GEMS',
    'Medihelp',
    'Momentum Health',
    'Polmed',
    'Profmed',
    'Sizwe Hosmed',
    'Self-pay / No medical aid',
    'Other',
  ];
  readonly bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  readonly chronicIllnesses = [
    'None disclosed',
    'Asthma',
    'Autoimmune disorder',
    'Cancer',
    'Chronic obstructive pulmonary disease (COPD)',
    'Diabetes',
    'Epilepsy',
    'Gastrointestinal disorder',
    'Hepatitis',
    'Heart disease',
    'HIV',
    'Hypertension',
    'Kidney disease',
    'Liver disease',
    'Mental health condition',
    'Multiple sclerosis',
    'Osteoarthritis',
    'Rheumatoid arthritis',
    'Sickle cell disease',
    'Thyroid disorder',
    'Tuberculosis',
    'Other',
  ];
  readonly emergencyContactRelationships = [
    'Spouse / Partner',
    'Parent / Guardian',
    'Child',
    'Sibling',
    'Relative',
    'Friend',
    'Colleague',
    'Caregiver',
    'Other',
  ];

  constructor(
    private fb: FormBuilder,
    private patientService: PatientService,
    private notification: NotificationService,
    private doctorProfileService: DoctorProfileService,
    private userManagementService: UserManagementService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.patientForm = this.fb.group({
      // User fields
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      gender: ['', Validators.required],
      title: [''],
      city: [''],
      addressLine1: [''],
      addressLine2: [''],
      addressLine3: [''],
      postalCode: [''],
      employer: [''],
      maritalStatus: [''],
      idNumber: [''],
      // Patient fields
      primaryDoctorName: [''],
      medicalAidScheme: [''],
      medicalAidNumber: [''],
      medicalAidPlan: [''],
      dateOfBirth: [''],
      medicalHistorySummary: [''],
      chronicIllness: [''],
      bloodType: [''],
      weight: [''],
      allergies: this.fb.array([]),
      // Payer fields
      payerFirstName: [''],
      payerLastName: [''],
      payerEmail: [''],
      payerPhoneNumber: [''],
      payerEmployer: [''],
      payerMaritalStatus: [''],
      payerIdNumber: [''],
      payerAddressLine1: [''],
      payerAddressLine2: [''],
      payerAddressLine3: [''],
      payerCity: [''],
      payerPostalCode: [''],
    });

    this.emergencyContactForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      relationship: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.patientId = this.route.snapshot.paramMap.get('patientId');
    this.isEditMode = !!this.patientId;
    this.loadUserProfile();

    if (this.isEditMode) {
      this.loadPatientForEdit(this.patientId!);
    } else {
      if (this.isReception) {
        this.loadLinkedDoctors();
      } else {
        this.loadDoctorName();
      }
    }
  }

  cancel(): void {
    this.router.navigate(['/dashboard/patients-view']);
  }

  private loadUserProfile(): void {
    const stored = localStorage.getItem('userProfile');
    this.activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';
    if (stored) {
      try {
        this.userProfile = JSON.parse(stored) as User;
      } catch {
        this.userProfile = null;
      }
    }
  }

  /**
   * Loads the list of doctors the current receptionist is linked to.
   * Auto-selects the membership if there is exactly one.
   */
  private loadLinkedDoctors(): void {
    if (!this.userProfile?.id) return;
    this.isDoctorListLoading = true;

    this.userManagementService.getEmployeeMemberships(this.userProfile.id).subscribe({
      next: (memberships) => {
        this.linkedDoctors = memberships.filter((m) => m.linked);
        this.isDoctorListLoading = false;

        if (this.linkedDoctors.length === 1) {
          // Auto-preselect the only available doctor
          this.selectedMembershipId = this.linkedDoctors[0].clinicMembershipId;
          this.patientForm.patchValue({
            primaryDoctorName: `Dr. ${this.linkedDoctors[0].doctorFirstName} ${this.linkedDoctors[0].doctorLastName}`,
          });
        }
      },
      error: () => {
        this.isDoctorListLoading = false;
        this.notification.error('Failed to load accessible doctors.');
      },
    });
  }

  onDoctorSelected(membershipId: string): void {
    this.selectedMembershipId = membershipId;
    const selected = this.linkedDoctors.find((d) => d.clinicMembershipId === membershipId);
    if (selected) {
      this.patientForm.patchValue({
        primaryDoctorName: `Dr. ${selected.doctorFirstName} ${selected.doctorLastName}`,
      });
    }
  }

  get allergies(): FormArray {
    return this.patientForm.get('allergies') as FormArray;
  }

  addAllergy() {
    this.allergies.push(this.fb.control(''));
  }

  removeAllergy(index: number) {
    this.allergies.removeAt(index);
  }

  loadDoctorName() {
    this.doctorProfileService.getDoctorProfileDetails().subscribe({
      next: (doctor) => {
        this.patientForm.patchValue({
          primaryDoctorName: `${doctor.user.firstName} ${doctor.user.lastName}`,
        });
      },
      error: () => {
        this.notification.error('Failed to load doctor details.');
      },
    });
  }

  private loadPatientForEdit(patientId: string): void {
    this.isLoading = true;
    this.patientService.getPatientById(patientId).subscribe({
      next: (patient) => {
        this.isLoading = false;
        this.populateForm(patient);
        this.loadEmergencyContactForEdit(patientId);
      },
      error: () => {
        this.isLoading = false;
        this.notification.error('Failed to load patient details.');
      },
    });
  }

  private populateForm(patient: Patient): void {
    this.allergies.clear();
    (patient.allergies ?? []).forEach((a) => this.allergies.push(this.fb.control(a)));

    this.patientForm.patchValue({
      firstName: patient.firstName,
      lastName: patient.lastName,
      email: patient.email,
      phoneNumber: patient.phoneNumber,
      gender: patient.gender ?? '',
      title: patient.title ?? '',
      maritalStatus: patient.maritalStatus ?? '',
      idNumber: patient.idNumber ?? '',
      primaryDoctorName: patient.primaryDoctorName ?? '',
      dateOfBirth: patient.dateOfBirth ?? '',
      medicalHistorySummary: patient.medicalHistorySummary ?? '',
      chronicIllness: patient.chronicIllness ?? '',
      bloodType: patient.bloodType ?? '',
      weight: patient.weight ?? '',
      addressLine1: patient.clinicPatient?.addressLine1 ?? '',
      addressLine2: patient.clinicPatient?.addressLine2 ?? '',
      addressLine3: patient.clinicPatient?.addressLine3 ?? '',
      city: patient.clinicPatient?.city ?? '',
      postalCode: patient.clinicPatient?.postalCode ?? '',
      employer: patient.clinicPatient?.employer ?? '',
      medicalAidScheme: patient.clinicPatient?.medicalAidScheme ?? '',
      medicalAidNumber: patient.clinicPatient?.medicalAidNumber ?? '',
      medicalAidPlan: patient.clinicPatient?.medicalAidPlan ?? '',
      payerFirstName: patient.clinicPatient?.payer?.firstName ?? '',
      payerLastName: patient.clinicPatient?.payer?.lastName ?? '',
      payerEmail: patient.clinicPatient?.payer?.email ?? '',
      payerPhoneNumber: patient.clinicPatient?.payer?.phoneNumber ?? '',
      payerEmployer: patient.clinicPatient?.payer?.employer ?? '',
      payerMaritalStatus: patient.clinicPatient?.payer?.maritalStatus ?? '',
      payerIdNumber: patient.clinicPatient?.payer?.idNumber ?? '',
      payerAddressLine1: patient.clinicPatient?.payer?.addressLine1 ?? '',
      payerAddressLine2: patient.clinicPatient?.payer?.addressLine2 ?? '',
      payerAddressLine3: patient.clinicPatient?.payer?.addressLine3 ?? '',
      payerCity: patient.clinicPatient?.payer?.city ?? '',
      payerPostalCode: patient.clinicPatient?.payer?.postalCode ?? '',
    });

    // Also restore the membership context for edit
    if (patient.clinicPatient?.clinicMembershipId) {
      this.selectedMembershipId = patient.clinicPatient.clinicMembershipId;
    }
  }

  private loadEmergencyContactForEdit(patientId: string): void {
    this.patientService
      .getEmergencyContactByPatientId(patientId)
      .pipe(catchError(() => of(null)))
      .subscribe((contact) => {
        if (!contact) {
          this.existingEmergencyContact = null;
          return;
        }

        this.existingEmergencyContact = contact;
        this.emergencyContactForm.patchValue({
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phoneNumber: contact.phoneNumber,
          relationship: contact.relationship,
        });
      });
  }

  private hasEmergencyContactData(): boolean {
    const { firstName, lastName, email, phoneNumber, relationship } =
      this.emergencyContactForm.value;
    return [firstName, lastName, email, phoneNumber, relationship].some(
      (value) => !!String(value ?? '').trim(),
    );
  }

  toggleEmergencyContactSameAsPayer() {
    this.emergencyContactSameAsPayer = !this.emergencyContactSameAsPayer;
    if (this.emergencyContactSameAsPayer) {
      const payer = this.patientForm.value;
      this.emergencyContactForm.patchValue({
        firstName: payer.payerFirstName,
        lastName: payer.payerLastName,
        email: payer.payerEmail,
        phoneNumber: payer.payerPhoneNumber,
        relationship: 'Self',
      });
    } else {
      this.emergencyContactForm.reset();
    }
  }

  togglePayerIsSameAsPatient() {
    this.payerIsSameAsPatient = !this.payerIsSameAsPatient;
    if (this.payerIsSameAsPatient) {
      const patient = this.patientForm.value;
      this.patientForm.patchValue({
        payerFirstName: patient.firstName,
        payerLastName: patient.lastName,
        payerEmail: patient.email,
        payerPhoneNumber: patient.phoneNumber,
        payerMaritalStatus: patient.maritalStatus,
        payerIdNumber: patient.idNumber,
        payerAddressLine1: patient.addressLine1,
        payerAddressLine2: patient.addressLine2,
        payerAddressLine3: patient.addressLine3,
        payerCity: patient.city,
        payerPostalCode: patient.postalCode,
        payerEmployer: patient.employer,
      });
    } else {
      this.patientForm.patchValue({
        payerFirstName: '',
        payerLastName: '',
        payerEmail: '',
        payerPhoneNumber: '',
        payerMaritalStatus: '',
        payerIdNumber: '',
        payerAddressLine1: '',
        payerAddressLine2: '',
        payerAddressLine3: '',
        payerCity: '',
        payerPostalCode: '',
        payerEmployer: '',
      });
    }
  }

  submit() {
    this.errorMessage = null;

    // Reception must select a doctor before saving (Q2)
    if (!this.isEditMode && this.isReception && !this.selectedMembershipId) {
      this.notification.error('Please select a doctor to register this patient under.');
      return;
    }

    if (this.patientForm.invalid) {
      this.patientForm.markAllAsTouched();
      return;
    }
    if (!this.isEditMode && this.emergencyContactForm.invalid) {
      this.emergencyContactForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    const form = this.patientForm.value;
    const ecForm = this.emergencyContactForm.value;
    const contact: EmergencyContact = {
      id: this.existingEmergencyContact?.id ?? '',
      firstName: ecForm.firstName,
      lastName: ecForm.lastName,
      email: ecForm.email,
      phoneNumber: ecForm.phoneNumber,
      relationship: ecForm.relationship,
    };

    const patient: Patient = {
      title: form.title,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phoneNumber: form.phoneNumber,
      gender: form.gender,
      dateOfBirth: form.dateOfBirth,
      chronicIllness: form.chronicIllness,
      medicalHistorySummary: form.medicalHistorySummary,
      weight: form.weight,
      bloodType: form.bloodType,
      maritalStatus: form.maritalStatus,
      idNumber: form.idNumber,
      allergies: form.allergies,
      clinicPatient: {
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        addressLine3: form.addressLine3,
        city: form.city,
        postalCode: form.postalCode,
        employer: form.employer,
        medicalAidScheme: form.medicalAidScheme,
        medicalAidNumber: form.medicalAidNumber,
        medicalAidPlan: form.medicalAidPlan,
        payer: {
          firstName: form.payerFirstName,
          lastName: form.payerLastName,
          email: form.payerEmail,
          phoneNumber: form.payerPhoneNumber,
          employer: form.payerEmployer,
          maritalStatus: form.payerMaritalStatus,
          idNumber: form.payerIdNumber,
          addressLine1: form.payerAddressLine1,
          addressLine2: form.payerAddressLine2,
          addressLine3: form.payerAddressLine3,
          city: form.payerCity,
          postalCode: form.payerPostalCode,
        },
        ...(this.isEditMode && this.hasEmergencyContactData() ? { emergencyContact: contact } : {}),
      },
    };

    if (this.isEditMode) {
      this.patientService.updatePatientFile(this.patientId!, patient).subscribe({
        next: () => {
          this.isLoading = false;
          this.notification.success('Patient record updated successfully.', 'Success');
          this.router.navigate(['/dashboard/patients-view']);
        },
        error: (err) => {
          this.isLoading = false;
          const msg =
            err?.error?.message ||
            'Failed to update patient record. Please check the information and try again.';
          this.errorMessage = msg;
          this.notification.error(msg, 'Update Failed');
        },
      });
    } else {
      // Pass the selected membership ID as a header so the backend scopes the ClinicPatient row correctly
      this.patientService
        .createPatientFile(patient, this.selectedMembershipId ?? undefined)
        .pipe(
          switchMap((createdPatient) =>
            this.patientService.createEmergencyContact(createdPatient.id!, contact),
          ),
        )
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.notification.success('Patient registered successfully.', 'Success');
            this.router.navigate(['/dashboard/patients-view']);
          },
          error: (err) => {
            this.isLoading = false;
            const msg =
              err?.error?.message ||
              'Failed to save patient record. Please check the required fields and try again.';
            this.errorMessage = msg;
            this.notification.error(msg, 'Registration Failed');
          },
        });
    }
  }
}
